import { Product, Sale } from "@/models/base";
import { AuthService, ModalService } from "@/services";
import { ISelfBilledSaleWrapper, SaleService } from "@/services/sale-service";
import { i18n } from "@/setup/i18n-setup";
import { TranslateResult } from "vue-i18n";
import { ConfirmResult } from "winfakt-vue-components";
import { printHumanFloat } from "../pretty-print";

async function  askToCreateProduct(product:Product):Promise<ConfirmResult>{
	let text = i18n.t("components.sales.self-billed-product-with-sku-does-not-exist-do-you-want-to-create-it", {sku: product.Sku});
	let yesText = i18n.t("common.yes");
	let noText = i18n.t("common.no");
	let cancelText = i18n.t("common.cancel");
	let title = i18n.t("common.warning");
	return AuthService.mainVueInstance.$wf.confirm(text, yesText, noText, title, "warning", cancelText);
}

async function convertNonExistingProductToDescriptionRow(sku:string, sale:Sale){
	for (let row of sale.Rows) {
		if (row.ProductSku != sku) {
			continue;
		}

		row.ProductSku = "";
		row.ProductDescription += ` (Sku: ${sku})`;
	}
}

async function createProduct(product:Product, sale:Sale):Promise<ConfirmResult>{
	let createdProduct = await ModalService.openCreateProductModal(product, {skuDisabled: true});
	if (!createdProduct) {
		return ConfirmResult.NO;
	}
	for (let row of sale.Rows) {
		if (row.ProductSku != product.Sku) {
			continue;
		}
		row.Product = createdProduct;
	}
	return ConfirmResult.YES;
}

async function processNonExistingProducts(saleWrapper:ISelfBilledSaleWrapper):Promise<ConfirmResult>{
	for (let product of saleWrapper.NonExistingProducts) {
		let ok = false;
		while(!ok){
			let result = await askToCreateProduct(product);
			if (result == ConfirmResult.CANCEL) {
				return ConfirmResult.CANCEL;
			}
			if (result == ConfirmResult.NO) {
				await convertNonExistingProductToDescriptionRow(product.Sku || "", saleWrapper.Sale);
				ok = true;
			}else{
				let result = await createProduct(product, saleWrapper.Sale);
				if (result == ConfirmResult.YES) {
					ok = true;
				}
			}
		}

	}
	return ConfirmResult.YES;
}


async function askToCreateContact(sale:Sale){
	let contact = sale.Contact;
	if (!contact) return;
	let name = contact.getDisplayName();
	let tag = "components.sales.peppol-contact-does-not-exist-do-you-want-to-create-it";
	let message = i18n.t(tag, {name});
	let confirm = await AuthService.mainVueInstance.$confirmBuilder(message).show();
	if (!confirm) {
		sale.Contact = null;
		sale.ContactID = 0;
		return;
	}
	let result = await ModalService.openEditContactModal(1, contact);
	if (!result || result.ID == 0){
		sale.Contact = null;
		sale.ContactID = 0;
		return;
	}
	sale.Contact = result;
	sale.ContactID = result.ID;
}

async function checkIfSelfBilledInvoiceExists(sale:Sale):Promise<ConfirmResult>{
	if (!sale.ContactID) return ConfirmResult.YES;
	let foundSale = await SaleService.getByContactIdAndReferenceNumberIfExists(sale.ContactID, sale.ContactsSaleReference);
	if (!foundSale) return ConfirmResult.YES;
	let message = i18n.t("components.sales.self-billed-sale-already-exists-with-number", {number: foundSale.ComputedFriendlyID});
	return AuthService.mainVueInstance.$wf.confirm(message, i18n.t("common.ok"), i18n.t("common.cancel"), i18n.t("common.warning"), "warning");
}

async function notifyNotMatchingTotals(saleWrapper:ISelfBilledSaleWrapper):Promise<ConfirmResult>{
	let messages:TranslateResult[] = [];
	if (Math.abs(saleWrapper.ReceivedTotalExcVAT - saleWrapper.Sale.TotalExcVAT) >= 0.01) {
		messages.push(i18n.t("components.sales.self-billed-received-total-exc-vat-does-not-match", {
			received: printHumanFloat(saleWrapper.ReceivedTotalExcVAT, 2),
			calculated: printHumanFloat(saleWrapper.Sale.TotalExcVAT, 2)
		}));
	}
	if (Math.abs(saleWrapper.ReceivedTotalIncVAT - saleWrapper.Sale.TotalIncVAT) >= 0.01){
		messages.push(i18n.t("components.sales.self-billed-received-total-inc-vat-does-not-match", {
			received: printHumanFloat(saleWrapper.ReceivedTotalIncVAT, 2),
			calculated: printHumanFloat(saleWrapper.Sale.TotalIncVAT, 2)
		}));
	}
	for (let message of messages) {
		let confirm = await AuthService.mainVueInstance.$wf.confirm(message, i18n.t("common.ok"), i18n.t("common.cancel"), i18n.t("common.warning"), "danger");
		if (!confirm) {
			return ConfirmResult.CANCEL;
		}
	}
	return ConfirmResult.YES;
}


export async function processSelfBilledSale(saleWrapper:ISelfBilledSaleWrapper):Promise<ConfirmResult>{
	if (saleWrapper.Sale.Contact && saleWrapper.Sale.Contact.ID == 0 && saleWrapper.Sale.ContactID == 0){
		await askToCreateContact(saleWrapper.Sale);
	}
	let confirm = await checkIfSelfBilledInvoiceExists(saleWrapper.Sale);
	if (confirm == ConfirmResult.NO) {
		return ConfirmResult.CANCEL;
	}
	confirm = await processNonExistingProducts(saleWrapper);
	if (confirm != ConfirmResult.YES){
		return confirm;
	}
	confirm = await notifyNotMatchingTotals(saleWrapper);
	return confirm;
}